import { useCallback } from 'react';
import { useHistory } from '@react-router';
import { useFeatureFlagsContext, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useWebsiteV2Feature } from '@shared/hooks/useWebsiteV2Feature';
import { withWindow } from '@shared/utils/withWindow';
import type { GoLiveChecksSource } from './GoLiveChecks.types';

/**
 * Query param that makes the website editor open the Go Live Checks wizard on arrival. Its value is
 * the `GoLiveChecksSource` that sent the couple there.
 */
export const GO_LIVE_CHECKS_QUERY_PARAM = 'goLiveChecks';

type UseGoLiveChecksRedirectOptions = Readonly<{
  /**
   * Reported on the wizard's shown event, so publishes can still be attributed to this surface.
   * Without one the hook is skipped — for callers that didn't opt in.
   */
  source: GoLiveChecksSource | undefined;
  /**
   * Skips the flag evaluation — for the guest site, where only a couple looking at their own
   * unpublished site should evaluate it.
   */
  skip?: boolean;
}>;

/**
 * For publish surfaces outside the website editor. While `websiteGoLiveChecksEnabled` is on,
 * turning publish on sends the couple to the website designer with the wizard open instead of
 * publishing in place. Unpublishing stays in place everywhere.
 *
 * - `isRedirectEnabled`: the flag is on and the event isn't WebsiteV2 (whose editor has no wizard).
 *   While false, callers keep their existing publish.
 * - `isRedirectLoading`: the flags haven't resolved, so neither answer is safe yet. Callers keep
 *   their publish control disabled, or an early click would publish in place and skip the wizard.
 */
export const useGoLiveChecksRedirect = ({ source, skip: skipOption = false }: UseGoLiveChecksRedirectOptions) => {
  const skip = skipOption || !source;
  const [isFlagEnabled, isFlagLoading] = useIsFeatureEnabled('websiteGoLiveChecksEnabled', { skip });
  const websiteV2 = useWebsiteV2Feature();
  const { eventHandle } = useEventInfo();
  const { formatToUrl } = useFeatureFlagsContext();
  const history = useHistory();

  const isRedirectLoading = !skip && (isFlagLoading || websiteV2.isLoading);
  const isRedirectEnabled = !skip && !isRedirectLoading && !!eventHandle && isFlagEnabled && !websiteV2.value;

  const redirectToGoLiveChecks = useCallback(() => {
    if (!source) return;
    const url = `/${eventHandle}/edit/design?${GO_LIVE_CHECKS_QUERY_PARAM}=${source}`;
    // Inside the admin app a push keeps the SPA warm, and the patched `history` re-adds `?feature.*`
    // overrides itself. The guest site is another bundle: a full navigation, formatted by hand so a
    // local flag override survives it.
    if (history.location.pathname.startsWith(`/${eventHandle}/edit`)) {
      history.push(url);
    } else {
      withWindow(window => window.location.assign(formatToUrl(url)));
    }
  }, [eventHandle, source, formatToUrl, history]);

  return { isRedirectEnabled, isRedirectLoading, redirectToGoLiveChecks };
};
